/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  $applyNodeReplacement,
  DOMConversion,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

export type SerializedMentionNode = Spread<
  {
    mentionName: string;
    mentionEmail: string;
    mentionValue: string;
  },
  SerializedTextNode
>;

const mentionStyle = '';

export class MentionNode extends TextNode {
  __mention: string;

  __email: string;

  __value: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__email, node.__value, node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(
      serializedNode.mentionName,
      serializedNode.mentionEmail,
      serializedNode.mentionValue
    ).updateFromJSON(serializedNode);
  }

  constructor(
    mentionName: string,
    mentionEmail: string,
    mentionValue: string,
    text?: string,
    key?: NodeKey
  ) {
    super(text ?? mentionName, key);
    this.__mention = mentionName;
    this.__email = mentionEmail;
    this.__value = mentionValue;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      mentionEmail: this.__email,
      mentionValue: this.__value,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionStyle;
    dom.className = 'text-litlingo-primary';
    dom.spellcheck = false;
    dom.title = this.__email;

    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    if (this.__text !== this.__mention) {
      element.setAttribute('data-lexical-mention-name', this.__mention);
      element.setAttribute('data-lexical-mention-email', this.__email);
      element.setAttribute('data-lexical-mention-value', this.__value);
    }
    element.textContent = this.__text;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement): DOMConversion<HTMLElement> | null => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: $convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  static isTextEntity(): true {
    return true;
  }

  static canInsertTextBefore(): boolean {
    return false;
  }

  static canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(
  mentionName: string,
  mentionEmail: string,
  mentionValue: string,
  textContent?: string
): MentionNode {
  const mentionNode = new MentionNode(
    mentionName,
    mentionEmail,
    mentionValue,
    textContent || mentionName
  );
  mentionNode.setMode('segmented').toggleDirectionless();
  return $applyNodeReplacement(mentionNode);
}

function $convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const { textContent } = domNode;
  // eslint-disable-next-line react/destructuring-assignment
  const mentionName = domNode.getAttribute('data-lexical-mention-name');
  // eslint-disable-next-line react/destructuring-assignment
  const mentionEmail = domNode.getAttribute('data-lexical-mention-email');
  // eslint-disable-next-line react/destructuring-assignment
  const mentionValue = domNode.getAttribute('data-lexical-mention-value');

  if (textContent !== null) {
    const node = $createMentionNode(
      typeof mentionName === 'string' ? mentionName : textContent,
      typeof mentionEmail === 'string' ? mentionEmail : textContent,
      typeof mentionValue === 'string' ? mentionValue : textContent,
      textContent
    );
    return {
      node,
    };
  }

  return null;
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
